import { useCallback, useState } from "react";

import { useApolloClient } from "@apollo/client";
import dayjs from "dayjs";

import { CreateCheckInput, TaskMetadataReason, TaskType } from "__graphql__/types";
import { PageName } from "analytics/events";
import { useCheckAlreadyCompletedToast } from "flows/Inventory/shared/hooks/useCheckAlreadyCompletedToast";
import { useChecksRedirect } from "flows/Inventory/shared/hooks/useChecksRedirect";
import { useStartNextCheck } from "flows/Inventory/shared/hooks/useStartNextCheck";
import { useTaskCompletedToast } from "flows/Inventory/shared/hooks/useTaskCompletedToast";
import { getPriorityGroup } from "flows/Inventory/shared/models/check/serializer";
import {
  CreateCheckDocument,
  CreateCheckMutation,
  CreateCheckMutationVariables,
} from "flows/Inventory/shared/queries/check/check.generated";
import { ErrorTypeForAnalytics, useAnalytics } from "shared/hooks/useAnalytics";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { useErrorToastWithAudio } from "shared/hooks/useErrorToastWithAudio";
import { reportErrorsToDDAndThrow } from "utils/datadog";
import { isNotNullNorUndefined, isNullOrUndefined } from "utils/tsHelpers";

import {
  ValidateBbdCheckDocument,
  ValidateBbdCheckMutation,
  ValidateBbdCheckMutationVariables,
} from "../queries/bbdCheck/bbdCheck.generated";
import { useBbdCheckStore } from "../stores/useBbdCheckStore";

export type UseBbdCheckMutationResult = {
  performCheckUpdate: () => Promise<void>;
  isUpdating: boolean;
};

// Read fresh state from Zustand at call time, not at render time
const getContext = () => useBbdCheckStore.getState();
const resetStore = () => useBbdCheckStore.getState().reset();

export function useBbdCheckMutation(): UseBbdCheckMutationResult {
  const client = useApolloClient();
  const [isUpdating, setIsUpdating] = useState(false);
  const { showCheckAlreadyCompletedToast } = useCheckAlreadyCompletedToast();
  const { showToastAndPlayError } = useErrorToastWithAudio({
    errorToastId: "stock_machine_error",
  });
  const { showToastUI } = useCustomToast();
  const showTaskCompletedToast = useTaskCompletedToast();
  const { navigateToCheck, navigateToOrigin } = useChecksRedirect();
  const { startNextCheck } = useStartNextCheck();
  const { sendSegmentTrackEvent } = useAnalytics();

  const showStockCheckCreatedToast = useCallback(() => {
    showToastUI({
      variant: "completed",
      title: "flows.inventory.bbd-checks.machines.actions.check-created-toast.body",
      duration: 2000,
    });
  }, [showToastUI]);

  const performCheckUpdate = useCallback(async () => {
    // Read the latest state at call time
    const context = getContext();

    if (isNullOrUndefined(context.checkId)) {
      throw new Error("No checkId present in context");
    }

    setIsUpdating(true);
    try {
      // Step 1: Validate BBD check
      await reportErrorsToDDAndThrow(
        "updating BBD check",
        async () => {
          const closestBBDAfterOutbound = context.closestBbd?.toISOString();
          try {
            await client.mutate<ValidateBbdCheckMutation, ValidateBbdCheckMutationVariables>({
              mutation: ValidateBbdCheckDocument,
              variables: {
                input: {
                  checkId: context.checkId!,
                  closestBBDAfterOutbound,
                  quantityExpired: context.expiredQuantity,
                  quantityTooGoodToGo: context.tooGoodToGoQuantity,
                },
              },
            });
          } catch (e: any) {
            if (e?.graphQLErrors?.[0]?.extensions?.code === "TASK_UPDATE_CONFLICT") {
              throw new Error("taskAlreadyCompleted");
            }
            throw e;
          }
        },
        {
          context: {
            checkId: context.checkId,
            sku: context.sku,
            origin: context.origin,
            priority: context.priority,
            shelfNumber: context.shelfNumber,
            shelfLetter: context.shelfLetter,
          },
        },
      );

      // Track finished
      const nextExpirationDate = context.closestBbd
        ? dayjs(context.closestBbd).format("YYYY-MM-DD")
        : undefined;
      sendSegmentTrackEvent("stockCheckFinished", {
        type: "automated",
        check_id: context.checkId,
        product_sku: context.sku,
        quantity_expected: context.productStock,
        quantity_counted: 0,
        quantity_damaged: 0,
        quantity_expired: context.expiredQuantity,
        quantity_tgtg: context.tooGoodToGoQuantity,
        next_expiration_date: nextExpirationDate,
      });

      // Step 2: Optionally create a stock check
      if (context.createCheckType !== null) {
        try {
          const input: Partial<CreateCheckInput> = {
            type: TaskType.stock_check,
            shelfNumber: `${context.shelfNumber}${context.shelfLetter}`,
            sku: context.sku!,
          };

          if (context.createCheckType === "NO_QUANTITY") {
            input.metadataReason = TaskMetadataReason.bbd_missing;
            input.priority = 7;
          } else {
            input.metadataReason = TaskMetadataReason.bbd;
            input.priority = 3;
          }

          await reportErrorsToDDAndThrow(
            "creating BBD check",
            async () => {
              await client.mutate<CreateCheckMutation, CreateCheckMutationVariables>({
                mutation: CreateCheckDocument,
                variables: {
                  input: input as CreateCheckInput,
                },
              });
            },
            {
              context: {
                checkId: context.checkId,
                sku: context.sku,
              },
            },
          );
          showStockCheckCreatedToast();
        } catch {
          // If creating check fails but validation succeeded, continue the flow
        }
      }

      // Step 3: If from activities page, just show toast and redirect
      if (context.origin === PageName.ACTIVITIES_PAGE) {
        showTaskCompletedToast();
        navigateToOrigin(context.origin);
        return;
      }

      // Step 4: Fetch next check
      try {
        const result = await startNextCheck({
          groupedPriority: getPriorityGroup(context.priority),
          shelfNumber: `${context.shelfNumber}${context.shelfLetter}`,
          type: [TaskType.bbd_check],
        });

        if (result.status === "noMoreCheck") {
          showTaskCompletedToast();
          navigateToOrigin(context.origin);
        } else if (result.status === "unknownError") {
          navigateToOrigin(context.origin);
        } else if (result.status === "checkAlreadyTaken") {
          navigateToOrigin(context.origin);
        } else if (isNotNullNorUndefined(result.check)) {
          navigateToCheck(result.check, context.origin);
        }
      } catch {
        navigateToOrigin(context.origin);
      }
    } catch (e: any) {
      if ((e?.message ?? e?.toString() ?? "").includes("taskAlreadyCompleted")) {
        showCheckAlreadyCompletedToast();
        sendSegmentTrackEvent("errorShown", {
          screen_name: PageName.INVENTORY_BBD_CHECK_LIST_PAGE,
          component_value: ErrorTypeForAnalytics.UNKNOWN,
        });
        navigateToOrigin(context.origin);
        return;
      }

      showToastAndPlayError();
      sendSegmentTrackEvent("errorShown", {
        screen_name: PageName.INVENTORY_BBD_CHECK_LIST_PAGE,
        component_value: ErrorTypeForAnalytics.UNKNOWN,
      });
    } finally {
      setIsUpdating(false);
      resetStore();
    }
  }, [
    client,
    showStockCheckCreatedToast,
    showTaskCompletedToast,
    navigateToOrigin,
    startNextCheck,
    navigateToCheck,
    showCheckAlreadyCompletedToast,
    showToastAndPlayError,
    sendSegmentTrackEvent,
  ]);

  return {
    performCheckUpdate,
    isUpdating,
  };
}
